// src/components/modal/ModalAction.tsx
import React from 'react';
import { View, Text } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { modalTokens } from './modal-tokens';

export type ModalActionVariant = 'neutral' | 'primary' | 'destructive' | 'success';

interface ModalActionProps {
  label: string;
  onPress: () => void;
  icon?: React.ReactNode;
  variant?: ModalActionVariant;
  disabled?: boolean;
  accessibilityLabel?: string;
}

export function ModalAction({
  label,
  onPress,
  icon,
  variant = 'neutral',
  disabled = false,
  accessibilityLabel,
}: ModalActionProps) {
  const colors = useThemeColors();

  const palette: Record<ModalActionVariant, { bg: string; fg: string }> = {
    neutral: { bg: colors.surface.secondary, fg: colors.text.secondary },
    primary: { bg: colors.primary[600], fg: colors.text.inverse },
    destructive: { bg: colors.traffic.redLight, fg: colors.error },
    success: { bg: colors.traffic.greenLight, fg: colors.success },
  };
  const { bg, fg } = palette[variant];

  return (
    <View
      style={{
        // grow to fill the action row and wrap: a 2-up grid on mobile, an even
        // wrapping row on web. Never a horizontal scroll.
        flexGrow: 1,
        flexBasis: isWeb ? 'auto' : '47%',
        minHeight: modalTokens.actionMinHeight,
        borderRadius: 12,
        backgroundColor: bg,
        overflow: 'hidden',
        // dim only on native: on web, AnimatedPressable already applies its own disabled opacity (would compound to 0.25)
        opacity: disabled && !isWeb ? 0.5 : 1,
      }}
    >
      {/* minHeight is repeated here intentionally: AnimatedPressable's native Animated.View wrapper doesn't forward flex, so the Pressable needs its own minHeight to fill the 48pt touch target. */}
      <AnimatedPressable
        onPress={onPress}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel ?? label}
        className="flex-row items-center justify-center gap-2 px-4"
        style={{ flex: 1, minHeight: modalTokens.actionMinHeight }}
      >
        {icon}
        <Text numberOfLines={1} style={{ color: fg, fontWeight: '600' }}>
          {label}
        </Text>
      </AnimatedPressable>
    </View>
  );
}
